import React, { useState, useEffect } from 'react';
import {
  Files,
  Search,
  Download,
  FileText,
  Image,
  FileSpreadsheet,
  File
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await axios.get(`${API_URL}/api/portal/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const spreadsheetTypes = ['xls', 'xlsx', 'csv'];
    
    if (imageTypes.includes(fileType)) return <Image className="h-8 w-8 text-emerald-500" />;
    if (spreadsheetTypes.includes(fileType)) return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
    if (fileType === 'pdf') return <FileText className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-blue-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      contract: 'bg-blue-100 text-blue-700',
      invoice: 'bg-emerald-100 text-emerald-700',
      proposal: 'bg-purple-100 text-purple-700',
      report: 'bg-amber-100 text-amber-700',
      other: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={colors[category] || colors.other}>{category}</Badge>;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Documents</h1>
        <p className="text-muted-foreground">Access shared documents and files</p>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Files className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <div className="text-3xl font-bold">{documents.length}</div>
              <div className="text-sm text-muted-foreground">Documents available</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Documents Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDocuments.map((doc) => (
          <Card key={doc.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                  {getFileIcon(doc.file_type)}
                </div>
                <Button variant="ghost" size="sm">
                  <Download className="h-4 w-4" />
                </Button>
              </div>
              <h3 className="font-medium text-sm truncate mb-1" title={doc.name}>
                {doc.name}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {formatFileSize(doc.file_size)} • {doc.file_type?.toUpperCase()}
              </p>
              <div className="flex items-center justify-between">
                {getCategoryBadge(doc.category)}
                <span className="text-xs text-muted-foreground">
                  {new Date(doc.created_at).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No documents available
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalDocuments;
